import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Button, Modal, Row, Col } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from 'react-redux'
import { useAuth } from '../../components/auth/useAuth'
import { getDelegations, addDelegation, editDelegation } from '../../actions/delegationsActions'
import { CustomDataTable } from '../../components/design/Datatables/CustomDataTable'
import Loader from '../../components/loaders/Loader'
import Swal from 'sweetalert2'

export default function Delegations() {
    const { t } = useTranslation()
    const location = useLocation()
    const dispatch = useDispatch()
    const { clientauth } = useAuth()

    const { delegations, loading, resultdelegation } = useSelector(state => state.delegations)

    const [modalShow, setModalShow] = useState(false)
    const [formState, setFormState] = useState({})
    const [isRegistering, setIsRegistering] = useState(false)
    const [isEditing, setIsEditing] = useState(false)

    const [page] = useState(1)
    const [limit] = useState(25)
    const [search, setSearch] = useState('')

    useEffect(() => {
        if (clientauth) {
            dispatch(getDelegations(page, limit, search))
        }
    }, [dispatch, clientauth, location, page, limit, search])

    const openModal = () => {
        setIsEditing(false)
        setFormState({
            id: '',
            nombre: '',
            active: true
        })
        setModalShow(true)
    }

    const selectDelegation = (delegation) => {
        setIsEditing(true)
        setFormState(delegation)
        setModalShow(true)
    }

    const handleInputChange = ({ target }) => {
        setFormState({
            ...formState,
            [target.name]: target.value
        })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setIsRegistering(true)
        if (isEditing) {
            await dispatch(editDelegation(formState._id, formState))
        } else {
            await dispatch(addDelegation(formState))
        }
        setIsRegistering(false)
    }

    const closeModal = () => {
        setModalShow(false)
    }

    useEffect(() => {
        if (resultdelegation) {
            dispatch({ type: 'DELEGATION_RESET_RESULT' })
            switch (resultdelegation) {
                case 'delegationCreated':
                    Swal.fire({
                        showConfirmButton: true,
                        showCancelButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('admin.delegations.addsuccess')
                    }).then(() => {
                        setModalShow(false)
                    })
                    break;
                case 'delegationUpdated':
                    Swal.fire({
                        showConfirmButton: true,
                        showCancelButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('admin.delegations.editsuccess')
                    }).then(() => {
                        setModalShow(false)
                    })
                    break;
                default:
                    Swal.fire({
                        showConfirmButton: true,
                        showCancelButton: false,
                        confirmButtonText: t('global.ok'),
                        icon: 'info',
                        text: resultdelegation
                    })
                    break;
            }
        }
    }, [resultdelegation, dispatch, t])

    const columnsDelegations = [
        {
            name: t('admin.delegations.id'),
            width: '100px',
            minWidth: '100px',
            cell: delegation =>
                <div className='cursor' onClick={() => selectDelegation(delegation)}>
                    {delegation.id}
                </div>,
            selector: (delegation) => delegation.id,
            sortable: true,
        },
        {
            name: t('admin.delegations.name'),
            minWidth: '200px',
            cell: delegation =>
                <div className='cursor' onClick={() => selectDelegation(delegation)}>
                    {delegation.nombre}
                </div>,
            selector: (delegation) => delegation.nombre,
            sortable: true,
        }, {
            name: t('global.actions'),
            width: '100px',
            minWidth: '100px',
            cell: delegation =>
                <div>
                    <i className='fas fa-pencil me-2 cursor' onClick={() => selectDelegation(delegation)}></i>
                </div>,
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        }
    ]

    const filteredDelegations = (delegations || []).filter(delegation =>
        delegation?.nombre?.toLowerCase().includes(search.toLowerCase()) ||
        String(delegation?.id ?? '').includes(search)
    )

    return (
        <>
            <div className="toolbar">
                <div className="toolbar-left">
                    <div className="input-group" style={{ maxWidth: '320px' }}>
                        <span className="input-group-text"><i className="fas fa-search"></i></span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder={t('admin.delegations.name')}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                </div>
                <div className="toolbar-right">
                    <Button variant="success" onClick={openModal}>
                        <i className="fas fa-plus"></i>{t('admin.delegations.add')}
                    </Button>
                </div>
            </div>

            {loading ? (
                <Loader />
            ) : (
                <div className="card-simple">
                    <CustomDataTable
                        columns={columnsDelegations}
                        data={filteredDelegations}
                        pagination={true}
                        exportable={true}
                        printable={true}
                        filter={true}
                    />
                </div>
            )}
            <Modal show={modalShow} onHide={closeModal} size="md" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? t('admin.delegations.edit') : t('admin.delegations.add')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={handleSubmit}>
                        <Row>
                            <Col xs={12} md={6} className="my-2">
                                <label>{t('admin.delegations.id')}</label>
                                <input
                                    className="form-control"
                                    type='number'
                                    name='id'
                                    value={formState.id ?? ''}
                                    onChange={handleInputChange}
                                    required
                                />
                            </Col>
                            <Col xs={12} md={6} className="my-2">
                                <label>{t('admin.delegations.name')}</label>
                                <input
                                    className="form-control"
                                    type='text'
                                    name='nombre'
                                    value={formState.nombre || ''}
                                    onChange={handleInputChange}
                                    required
                                />
                            </Col>
                        </Row>
                        <Row className="mt-4">
                            <Col className="d-flex justify-content-end align-items-center">
                                <Button variant="secondary" onClick={closeModal} className="me-2">
                                    <i className="fas fa-times me-2"></i>{t('global.cancel')}
                                </Button>
                                <Button variant="primary" type="submit" disabled={isRegistering}>
                                    <i className="fas fa-save me-2"></i>{t('global.save')}
                                </Button>
                            </Col>
                        </Row>
                    </form>
                </Modal.Body>
            </Modal>
        </>
    )
}